import './dashboard_help.scss';
import { OverviewTabHelpDetails } from 'app/overview/OverviewService';
import {
    PLACEMENT_BOTTOM,
    PLACEMENT_RIGHT,
    TOUR_TYPE__NAVIGATED_TOUR,
} from 'app/shared/help_and_information/tours/TourService';
import { HelpDetails } from 'app/shared/help_and_information/HelpRegistry';
import {
    DASHBOARD_ITEM_HELP_KEY,
    DASHBOARD_LEGEND_HELP_KEY,
} from 'app/overview/dashboard/dashboard_constants';
import {
    NAV_TARGET_DASHBOARD_TAB,
    NAV_TARGET_OVERVIEW,
} from 'app/shared/internal_navigation/nav_targets';

export const dashboardHelpDetails: OverviewTabHelpDetails = {
    header: 'Editing a Dashboard',
    tags: ['Dashboard'],
    quickStartGuideSearchText: `overview-user-interface#dashboard-tab-functionality-1`,
    descriptionHtml: `<p>The Dashboard is used for analysis and reporting. There are 3 components that make up the dashboard:</p>
<p>1. Dashboard itself contains the current <span class="ims-name">Dashboard Tabs</span> for the user. The user can add and remove tabs from their dashboard.</p>
<p>2. <span class="ims-name">Dashboard Tabs</span> group information together to be viewed on a single screen.</p>
<p>3. <span class="ims-name">Dashboard Items</span> can be charts, status indicators or table views of data contained in IMS.</p>`,
    innerTourSteps: [
        {
            title: 'Manage Tabs',
            content: `This button opens a panel for managing all owned tabs and tabs that have been shared by other users.`,
            target: 'manage-tabs-button',
            placement: PLACEMENT_BOTTOM,
        },
        {
            title: 'Dashboard Tabs List',
            content: `<p>Current <span class="ims-name">dashboards tabs</span> will be visible here, between the <span class="ims-name">Manage Tabs</span> 
and <span class="ims-name">New tab</span> buttons. If there is nothing here
                      try clicking <span class="ims-name">New Tab</span>.</p>
<p>Each <span class="ims-name">dashboard tab</span> will have the following button:</p>
<p><span class="glyphicon glyphicon-remove"></span> Close the <span class="ims-name">dashboard tab</span>.
Note this is not the same as delete, the <span class="ims-name">dashboard tab</span> will still be accessible from the
<span class="ims-name">manage tabs</span> panel.</p>`,
            target: 'dashboard-tabs-container',
            placement: PLACEMENT_BOTTOM,
        },
        {
            title: 'Action Buttons',
            content: `<p>The following actions can be performed on the selected <span class="ims-name">dashboards tab</span>:</p>
<p><span class="glyphicon glyphicon-print"></span> Print the <span class="ims-name">dashboard tab</span>.</p>
<p><span class="glyphicon glyphicon-trash"></span> Permanently delete the <span class="ims-name">dashboard tab</span>.</p>
<p><span class="glyphicon glyphicon-pencil"></span> Edit the <span class="ims-name">dashboard tab</span>'s name.</p>`,
            target: 'dashboard-tab-details',
            placement: PLACEMENT_RIGHT,
        },
        {
            title: 'New Tab',
            content: `This button will add a new blank <span class="ims-name">dashboard tab</span>`,
            target: 'new-dashboard-tab-btn',
            placement: PLACEMENT_BOTTOM,
        },
        {
            title: 'New <span class="ims-name">dashboard item</span>',
            content: `
<div class="dashboard-tour-contents">
<p>Hover over this item to be presented with a list of item types. Click on the item to add it to the current tab.</p>
<p>The available items are:</p>
<ul>
<li><img src="assets/icons/add-chart.svg"/><span class="ims-name">Chart</span> Line or bar chart</li>
<li><img src="assets/icons/add-status.svg"/><span class="ims-name">Status</span> Colour coded indicators</li>
<li><img src="assets/icons/add-table.svg"/><span class="ims-name">Table</span> Numerical values in a grid format</li>
</ul>
</div>`,
            target: 'dashboard-new-panel-buttons',
            placement: PLACEMENT_RIGHT,
        },
    ],
    // Enable the add item button so we can put a tour bubble on it.
    // Events from the button are appropriately handled in all app states in the
    // dashboard service
    onStart: () => {
        (window as any).services.dashboardService.tourEnabled = true;
    },
    onEnd: () => {
        (window as any).services.dashboardService.tourEnabled = false;
    },
};

export const MANAGE_DASHBOARD_TABS_HELP_KEY = 'manageDashboardTabs';

export const manageDashboardTabHelpDetails: HelpDetails = {
    key: MANAGE_DASHBOARD_TABS_HELP_KEY,
    navigateTo: [NAV_TARGET_OVERVIEW, NAV_TARGET_DASHBOARD_TAB],
    header: 'Manage Dashboard Tabs',
    quickStartGuideSearchText: 'managing-and-sharing-tabs',
    tags: ['Dashboard'],
    descriptionHtml: `<p>The dashboard is the list of <span class="ims-name">dashboard tabs</span> that are currently being viewed. 
This panel allows management of which tabs are in the dashboard, manage the library of tabs and manage sharing tabs amongst users.</p>
`,
    tourType: TOUR_TYPE__NAVIGATED_TOUR,
    innerTourSteps: [
        {
            title: 'Filter tabs',
            content: `Filter the <span class="ims-name">dashboard tabs</span> by name and owner`,
            target: 'manage-dashboard-tabs-filter',
            placement: PLACEMENT_BOTTOM,
        },
        {
            title: 'Tab',
            content: `The name of the <span class="ims-name">dashboard tab</span>`,
            target: 'manage-dashboard-tabs-tab-name',
            placement: PLACEMENT_BOTTOM,
        },
        {
            title: 'Owner',
            content: `<p>The owner of the <span class="ims-name">dashboard tab</span>.</p>
<p>Only owners can make changes to a <span class="ims-name">dashboard tab</span>. Read only users will see a <span class="glyphicon glyphicon-lock"></span> to indicate no
writing is allowed.</p>`,
            target: 'manage-dashboard-tabs-owner',
            placement: PLACEMENT_BOTTOM,
        },
        {
            title: 'Last Updated',
            content: `Timestamp of the last time the tab was updated`,
            target: 'manage-dashboard-tabs-last-updated',
            placement: PLACEMENT_BOTTOM,
        },
        {
            title: 'Delete',
            content: `
<p>This will permanently delete a <span class="ims-name">dashboard tab</span>.</p>
<p>Only the tab's owner can delete a tab.</p>`,
            target: 'manage-dashboard-tabs-delete',
            placement: PLACEMENT_BOTTOM,
        },
        {
            title: 'Sharing',
            content: `
<p>This will turn on and off read sharing for other users.
When a tab is shared, users other than the owner can add the tab to their dashboard.
Any changes made by the owning user will be viewable by other users.</p>
<p>Only the tab's owner can change the sharing status of a tab.</p>`,
            target: 'manage-dashboard-tabs-sharing',
            placement: PLACEMENT_BOTTOM,
        },
        {
            title: 'Copy',
            content: `Tabs can be copied so they may be independently modified.`,
            target: 'manage-dashboard-tabs-copy',
            placement: PLACEMENT_BOTTOM,
        },
        {
            title: 'Add',
            content: `Add the tab to the dashboard.`,
            target: 'manage-dashboard-tabs-add',
            placement: PLACEMENT_BOTTOM,
        },
    ],

    beforeStart: () => {
        (window as any).services.dashboardService.openManageTabPanel();
        return 250;
    },
};

export const DASHBOARD_ITEM_SETTINGS_HELP_KEY = 'dashboardItemSettingsHelp';

export const dashboardDataSourceSettingsHelp: HelpDetails = {
    header: 'Dashboard Item Settings',
    tags: ['Dashboard'],
    quickStartGuideSearchText: 'adding-charts-to-tabs#datasource-settings-item-options-2',
    key: DASHBOARD_ITEM_SETTINGS_HELP_KEY,
    descriptionHtml: `<p><span class="ims-name">Dashboard items</span> are highly configurable components. The <span class="ims-name">datasource settings</span> panel is the main
control used to determine how data is visualized.</p>
<p>Users can select different <span class="ims-name">item types</span> that changes how the selected data is presented. Each <span class="ims-name">item type</span>
has a different strengths for analysing data.</p>
<p>The name for a piece of data with an associated timestamp in IMS is called a 'stat'.</p>
<p>The <span class="ims-name">dashboard item</span> displays a calculated data set containing calculated stats. This calculated data set originates from a source data set containing source stats.</p>
<p>The <span class="ims-name">data source</span> chooses the type of stats to be used in the calculation.</p>
<p>The <span class="ims-name">group by</span> setting is used to assign source stats against <span class="ims-name">assets</span>, <span class="ims-name">IP devices</span>, or other groups when calculating the data set.</p>
<p>The <span class="ims-name">min/max axis value</span> settings are used to specify the minimum and maximum values used on the Y axis. If the data contains values outside of this range, the range will be adjusted accordingly.</p>
<p>The <span class="ims-name">start time</span> and <span class="ims-name">end time</span>, only includes source stats that lie within the selected times in the calculation.</p>
<p>The <span class="ims-name">time slize size</span> chooses the time period of each calculated stat.</p>
<p>The <span class="ims-name">filter by</span> setting includes source stats that match the chosen filters.</p>`,
    navigateTo: [NAV_TARGET_OVERVIEW, NAV_TARGET_DASHBOARD_TAB],
    tourType: TOUR_TYPE__NAVIGATED_TOUR,
    innerTourSteps: [
        {
            target: 'item-header-mock-help-item-uuid',
            title: 'Item Header',
            content: `<p>The item header describes what is shown in the <span class="ims-name">dashboard item</span>.</p>
<p>When the <span class="ims-name">Auto</span> checkbox is enabled, the item header will be a combination of the <span class="ims-name">data source</span>
and <span class="ims-name">group by</span> settings</p>
<p>When the <span class="ims-name">Auto</span> checkbox is disabled, the user may enter their own text to describe the <span class="ims-name">dashboard item</span></p>`,
            placement: PLACEMENT_RIGHT,
            yOffset: -15,
        },
        {
            target: 'item-type-mock-help-item-uuid',
            title: 'Item Type',
            content: `<p>The <span class="ims-name">item type</span> determines what sort of data visualisation is used. There are 3 selectable types.</p>
<p><span class="ims-name">Charts</span> plot data against time. This are useful high level analysis.</p>
<p><span class="ims-name">Tables</span> show data in a excel-like format. 
The data can be exported as CSV. This is useful for low level analysis.</p>
<p><span class="ims-name">Status</span> shows traffic light colours for each <span class="ims-name">group by</span> data set at the currently selected time. 
This is useful for seeing the health of the system at a glance.</p>`,
            placement: PLACEMENT_RIGHT,
            yOffset: -15,
        },
        {
            target: 'chart-display-type-mock-help-item-uuid',
            title: 'Chart Display Type',
            content: `<p>The <span class="ims-name">chart</span> item type has this special sub option, <span class="ims-name">chart type</span>. Each
selectable type shows a different kind of chart to aid analysis.</p>
<p><span class="ims-name">Trend line</span> charts show each <span class="ims-name">group by</span> data set as a separate line of the chosen data source metric against time.</p>
<p><span class="ims-name">Trend bar</span> charts show a vertical bar for each <span class="ims-name">time slice</span>, for each <span class="ims-name">group by</span> data set, against time.</p>
<p><span class="ims-name">Accumulated bar</span> charts show a single vertical bar per <span class="ims-name">group by</span> aggregation. The aggregation is calculated between the 
<span class="ims-name">start time</span> and <span class="ims-name">end time</span> settings.</p>`,
            placement: PLACEMENT_RIGHT,
            yOffset: -15,
        },
        {
            target: 'data-source-mock-help-item-uuid',
            title: 'Data Source',
            content: `
<p>The <span class="ims-name">data source</span> is type of stats that will be used to create the calculated data set.</p>
<p>IMS allows the user to set stats collected from certain <span class="ims-name">assets</span> or <span class="ims-name">IP devices</span> to be ignored. The <span class="ims-name">ignored stats</span> setting will determine whether
these ignored stats are included in the calculated data set.</p>
<p>Depending on the <span class="ims-name">data source</span> selected, other options will appear:</p>
<p><span class="ims-name">Active radio stats</span> (radio source data only) only includes stats that match the criteria of this setting.</p>
<p><span class="ims-name">Include subgroup</span> will provide additional grouping on the subgroup field.</p>
            `,
            placement: PLACEMENT_RIGHT,
            yOffset: -15,
        },
        {
            target: 'group-by-mock-help-item-uuid',
            title: 'Group By',
            content: `
<p>The <span class="ims-name">group by</span> setting is used to assign source stats against <span class="ims-name">assets</span>, 
<span class="ims-name">IP devices</span>, or other groups when calculating the data set.</p>
            `,
            placement: PLACEMENT_RIGHT,
            yOffset: -15,
        },
        {
            target: 'start-time-mock-help-item-uuid',
            title: 'Start Time',
            content: `<p>Only includes stats after the <span class="ims-name">start time</span> in the data set calculation.</p>`,
            placement: PLACEMENT_RIGHT,
            yOffset: -15,
        },
        {
            target: 'end-time-mock-help-item-uuid',
            title: 'End Time',
            content: `<p>Only includes stats before the <span class="ims-name">end time</span> in the data set calculation.</p>`,
            placement: PLACEMENT_RIGHT,
            yOffset: -15,
        },
        {
            target: 'time-slice-size-mock-help-item-uuid',
            title: 'Time Slice Size',
            content: `<p>The <span class="ims-name">time slice size</span> sets the time period of each calculated stat. A longer <span class="ims-name">time slice size</span>
will result in more source stats being included in the final aggregation for the calculated stat.</p>`,
            placement: PLACEMENT_RIGHT,
            yOffset: -15,
        },
        {
            target: 'filter-by-mock-help-item-uuid',
            title: 'Filter By',
            content: `<p>Only includes stats that match the filter criteria.</p>
<p>If the <span class="ims-name">Selected</span> checkbox is enabled, stats will be filtered by the map selection or the
assets list selection in the center panel.</p>`,
            placement: PLACEMENT_RIGHT,
            yOffset: -15,
        },
    ],
    beforeStart: () => {
        (window as any).services.dashboardService.openMockSettingsPanel();
        return 500;
    },
    onEnd: () => {
        (window as any).services.dashboardService.closeMockSettingsPanel();
    },
    onClose: () => {
        (window as any).services.dashboardService.closeMockSettingsPanel();
    },
};

export const dashboardLegendHelp: HelpDetails = {
    header: 'Dashboard Item Displayed Data List',
    tags: ['Dashboard'],
    key: DASHBOARD_LEGEND_HELP_KEY,
    descriptionHtml: `
<p>The displayed data list:</p>
<p>De-clutters very large data sets by showing and hiding data sets for a <span class="ims-name">group by</span> aggregation.</p>
<p>Allows navigation of very large data by switching pages.</p>
<p>Provides a legend to help match line or bar colours for each <span class="ims-name">group by</span> aggregation.</p>
<p>Note that the settings in the displayed data list are <span class="ims-name">never saved between sessions</span>. If the
chosen displayed items to persist, please use the <span class="ims-name">datasource settings filter</span>.</p>
`,
    navigateTo: [NAV_TARGET_OVERVIEW, NAV_TARGET_DASHBOARD_TAB],
    tourType: TOUR_TYPE__NAVIGATED_TOUR,
    innerTourSteps: [
        {
            target: 'search-legend-mock-help-item-uuid',
            title: 'Search',
            content: 'Filter the displayed data list ',
            placement: PLACEMENT_RIGHT,
            yOffset: -15,
        },
        {
            target: 'legend-show-btn-mock-help-item-uuid',
            title: 'Show',
            content: `<p>The show button will enable showing for all of the items currently in the 
<span class="ims-name">displayed data list</span>.</p>
<p>This takes into account the <span class="ims-name">displayed data list</span> filter.</p>`,
            placement: PLACEMENT_BOTTOM,
        },
        {
            target: 'legend-hide-btn-mock-help-item-uuid',
            title: 'Hide',
            content: `<p>The hide button will disable showing for all of the items currently in the 
<span class="ims-name">displayed data list</span>.</p>
<p>This takes into account the <span class="ims-name">displayed data list</span> filter.</p>`,
            placement: PLACEMENT_BOTTOM,
        },
        {
            target: 'legend-page-size-mock-help-item-uuid',
            title: 'Page Size',
            content: `<p>Sets the number of items to display in each page.</p>`,
            placement: PLACEMENT_BOTTOM,
        },
        {
            target: 'legend-entries-mock-help-item-uuid',
            title: 'Displayed Data List Entries',
            content: `<p>All of the displayed <span class="ims-name">group by</span> aggregations will appear here
when their data is loaded. The user is able to click on each row toggle showing for that item.</p>`,
            placement: PLACEMENT_BOTTOM,
        },
    ],
    beforeStart: () => {
        (window as any).services.dashboardService.openMockLegendPanel();
        return 500;
    },
    onEnd: () => {
        (window as any).services.dashboardService.closeMockLegendPanel();
    },
    onClose: () => {
        (window as any).services.dashboardService.closeMockLegendPanel();
    },
};

export const dashboardItemHelp: HelpDetails = {
    header: 'Dashboard Item',
    tags: ['Dashboard'],
    quickStartGuideSearchText: 'adding-charts-to-tabs#item-types-3',
    key: DASHBOARD_ITEM_HELP_KEY,
    descriptionHtml: `<p><span class="ims-name">Dashboard items</span> are the windows inside <span class="ims-name">Dashboard Tabs</span>. 
Dashboard Items can be configured to show information from different data sources, visualised in several ways.</p>
<p>Click and drag on the <span class="ims-name">Dashboard Item</span> header to rearrange the Item inside the Tab.</p>
<p>Click and drag on the <span class="ims-name">Dashboard Item</span> lower right corner to resize the Item.</p>`,
    navigateTo: [NAV_TARGET_OVERVIEW, NAV_TARGET_DASHBOARD_TAB],
    tourType: TOUR_TYPE__NAVIGATED_TOUR,
    innerTourSteps: [
        {
            target: 'dashboard-item-btns-mock-help-item-uuid',
            title: 'Dashboard Item Header',
            content: `
<p>The title of the <span class="ims-name">Dashboard Item </is configurable in the 
<span class="glyphicon glyphicon-cog"> </span> settings panel.</p>
<p>The time frame of data contained in the <span class="ims-name">dashboard item</span> is displayed to the right
of the header text.</p>
            `,
            placement: PLACEMENT_BOTTOM,
            yOffset: -30,
        },
        {
            target: 'dashboard-item-btns-mock-help-item-uuid',
            title: 'Action Buttons',
            content: `
<p>The contents of this row of buttons will change depending on what type of <span class="ims-name">dashboard item</span> is chosen to be 
displayed and whether there is write access to this <span class="ims-name">Dashboard Tab.</span></p>
<p><span class="glyphicon glyphicon-cog"> </span> Open the <span class="ims-name">Dashboard Item </Settings panel.</p>
<p><span class="glyphicon glyphicon-floppy-disk"> </span> Export the displayed data as CSV (Table item only).</p>
<p><span class="glyphicon glyphicon-duplicate"> </span> Print the <span class="ims-name">dashboard item</span>.</p>
<p><span class="glyphicon glyphicon-filter"> </span> Toggle display of current filters. Filters are set in the settings panel.</p>
<p><span class="glyphicon glyphicon-resize-full"> </span> Expand <span class="ims-name">Dashboard Table.</span></p>
<p><span class="glyphicon glyphicon-resize-small"> </span> Shrink <span class="ims-name">Dashboard Table.</span></p>
<p><img style="height:13px;position:relative;top:-1px;" src="assets/icons/undo.svg"/> Undo the last <span class="glyphicon glyphicon-cog"></span> <span class="ims-name">data source setting</span> or <span class="ims-name">displayed data list</span> change.</p>
<p><img style="height:13px;position:relative;top:-1px;" src="assets/icons/redo.svg"/> </span> Redo the last undone <span class="glyphicon glyphicon-cog"></span> <span class="ims-name">data source setting</span> or 
<span class="ims-name">displayed data list</span> change.</p>
            `,
            placement: PLACEMENT_BOTTOM,
        },
        {
            target: 'dashboard-item-page-btn-mock-help-item-uuid',
            title: 'Page Controls',
            content: `<p>The page controls are used when there are too many data sets to be displayed on a single chart or bar graph.</p>
<p>The <span class="glyphicon glyphicon-chevron-left"></span> decreases the current page by 1</p>
<p>The <span class="ims-name">Page X of Y</span> text can be clicked to open up the <span class="ims-name">displayed data list</span> widget</p>
<p>The <span class="glyphicon glyphicon-chevron-right"></span> increases the current page by 1</p>`,
            placement: PLACEMENT_RIGHT,
            yOffset: -22,
            xOffset: -12,
        },
        {
            target: 'dashboard-item-page-btn-mock-help-item-uuid',
            title: 'Delete <span class="ims-name">dashboard item</span>',
            content: `This will permanently delete the <span class="ims-name">Dashboard Item</span> 
from the <span class="ims-name">Dashboard Tab</span>.`,
            placement: PLACEMENT_RIGHT,
            yOffset: -51,
            xOffset: -12,
        },
    ],
    beforeStart: () => {
        (window as any).services.dashboardService.openMockDashboardItem();
        return 500;
    },
    onEnd: () => {
        (window as any).services.dashboardService.closeMockDashboardItem();
    },
    onClose: () => {
        (window as any).services.dashboardService.closeMockDashboardItem();
    },
};
